import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Coins, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, trackPageView } from '../services/analytics';
import { useSession } from '../context/SessionContext';
import { purchaseCreditRefill, getSageCredits, getCreditIdsForPlatform } from '../services/iapService';
import API_URL from '../config/apiConfig';

// Get platform-specific credit IDs
const creditIds = getCreditIdsForPlatform();

const CREDIT_PACKS = [
  {
    id: creditIds.CREDITS_500,
    coins: 500,
    price: 500,
    questions: 5,
    tag: null,
    color: '#92FFF7',
  },
  {
    id: creditIds.CREDITS_1000,
    coins: 1000,
    price: 1000,
    questions: 10,
    tag: 'POPULAR',
    color: '#74B3FF',
  },
  {
    id: creditIds.CREDITS_1500,
    coins: 1500,
    price: 1500,
    questions: 15,
    tag: 'BEST VALUE',
    color: '#c084fc',
  },
];

const CreditsRefill = () => {
  const navigate = useNavigate();
  const { user } = useSession();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [addedAmount, setAddedAmount] = useState(0);

  const userId = user?.user_id || localStorage.getItem('user_id');

  const fetchCredits = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/api/sage/credits/${userId}`);
      const data = await res.json();
      if (data.success) {
        setCredits(data.credits || 0);
        localStorage.setItem('sage_credits', String(data.credits || 0));
      }
    } catch {
      setCredits(getSageCredits());
    }
  }, [userId]);

  useEffect(() => {
    fetchCredits();
    const handler = () => fetchCredits();
    window.addEventListener('credits-updated', handler);
    return () => window.removeEventListener('credits-updated', handler);
  }, [fetchCredits]);

  useEffect(() => {
    trackPageView('credits_refill');
    trackEvent('credits_page_viewed', { current_credits: localStorage.getItem('sage_credits') || '0' });
  }, []);

  const handlePurchase = async (pack) => {
    if (!userId) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
    if (loading) return;

    setLoading(pack.id);

    try {
      const result = await purchaseCreditRefill(pack.id, userId);

      if (result.redirected) {
        toast.info('Complete payment on taraanaya.ai');
        setLoading(null);
        return;
      }

      if (result.domain_blocked) {
        toast.error(result.message || 'Payments only work on taraanaya.ai or the app', { duration: 6000 });
        setLoading(null);
        return;
      }

      if (result.success) {
        setAddedAmount(result.credits_added || pack.coins);
        setShowSuccess(true);
        await fetchCredits();
        toast.success(`+${result.credits_added || pack.coins} Sage Coins added!`);
        setTimeout(() => setShowSuccess(false), 2500);
      } else if (result.cancelled) {
        // silent
      } else {
        toast.error(result.message || 'Payment failed');
      }
    } catch (e) {
      if (e.message !== 'Payment cancelled') {
        toast.error('Payment failed. Please try again.');
      }
    } finally {
      setLoading(null);
    }
  };

  const particles = [...Array(15)].map((_, i) => ({
    id: i,
    size: Math.random() * 2.5 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    dur: 3 + Math.random() * 2,
    delay: Math.random() * 2,
    color: i % 3 === 0 ? '#92FFF7' : i % 3 === 1 ? '#74B3FF' : '#5F42C9',
  }));

  return (
    <div
      data-testid="credits-refill-page"
      style={{
        height: '100vh',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Nebula glow */}
      <motion.div
        style={{
          position: 'absolute',
          width: 500,
          height: 500,
          background: 'radial-gradient(circle, rgba(116,179,255,0.07) 0%, rgba(95,66,201,0.04) 50%, transparent 80%)',
          filter: 'blur(70px)',
          left: '50%',
          top: '25%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            left: `${p.x}%`,
            top: `${p.y}%`,
            pointerEvents: 'none',
          }}
          animate={{ y: [0, -12, 0], opacity: [0.15, 0.45, 0.15] }}
          transition={{ duration: p.dur, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
        />
      ))}

      {/* Success overlay */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(10,14,23,0.95)',
            }}
          >
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{ textAlign: 'center' }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.1 }}
                style={{
                  width: 64,
                  height: 64,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(116,179,255,0.15)',
                  border: '2px solid #74B3FF',
                }}
              >
                <Check size={32} color="#74B3FF" />
              </motion.div>
              <p style={{ fontSize: 28, fontWeight: 700, color: '#74B3FF', marginBottom: 6 }}>
                +{addedAmount}
              </p>
              <p style={{ fontSize: 14, color: 'rgba(226,232,240,0.6)' }}>Sage Coins Added</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 420,
          margin: '0 auto',
          padding: '24px 16px',
          flex: 1,
          overflowY: 'auto',
          width: '100%',
        }}
      >
        {/* Back */}
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          data-testid="credits-back-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(116,179,255,0.1)',
            border: '1px solid rgba(116,179,255,0.2)',
            borderRadius: 10,
            padding: '8px 14px',
            color: '#E2E8F0',
            fontSize: 13,
            cursor: 'pointer',
            marginBottom: 28,
          }}
        >
          <ArrowLeft size={16} />
          Back
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          style={{ textAlign: 'center', marginBottom: 28 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <Coins size={22} color="#74B3FF" />
            <h1
              style={{
                fontSize: 24,
                fontWeight: 600,
                color: '#E2E8F0',
                letterSpacing: 0.5,
              }}
            >
              Sage Coins
            </h1>
          </div>
          <p
            style={{
              fontSize: 13,
              color: 'rgba(226,232,240,0.45)',
              maxWidth: 260,
              margin: '0 auto',
              lineHeight: 1.5,
            }}
          >
            100 coins = 1 question to the Sage
          </p>
        </motion.div>

        {/* Current balance card */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          data-testid="credits-balance-card"
          style={{
            background: 'rgba(116,179,255,0.06)',
            border: '1px solid rgba(116,179,255,0.15)',
            borderRadius: 16,
            padding: '20px 24px',
            marginBottom: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <p style={{ fontSize: 11, color: 'rgba(226,232,240,0.4)', fontWeight: 500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
              Current Balance
            </p>
            <p data-testid="credits-balance-value" style={{ fontSize: 32, fontWeight: 700, color: '#74B3FF', lineHeight: 1 }}>
              {credits}
            </p>
          </div>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'rgba(116,179,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Coins size={22} color="#74B3FF" />
          </div>
        </motion.div>

        {/* Pack cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
          {CREDIT_PACKS.map((pack, idx) => {
            const isPopular = pack.tag === 'POPULAR';
            const isBest = pack.tag === 'BEST VALUE';
            const isProcessing = loading === pack.id;

            return (
              <motion.div
                key={pack.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + idx * 0.1 }}
                data-testid={`credit-pack-${pack.id}`}
                style={{
                  position: 'relative',
                  background: isPopular
                    ? 'rgba(116,179,255,0.08)'
                    : isBest
                    ? 'rgba(192,132,252,0.06)'
                    : 'rgba(116,179,255,0.03)',
                  border: isPopular
                    ? '1px solid rgba(116,179,255,0.3)'
                    : isBest
                    ? '1px solid rgba(192,132,252,0.25)'
                    : '1px solid rgba(116,179,255,0.1)',
                  borderRadius: 16,
                  padding: 20,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => !loading && handlePurchase(pack)}
              >
                {/* Tag badge */}
                {pack.tag && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -10,
                      right: 20,
                      background: isPopular
                        ? 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)'
                        : 'linear-gradient(135deg, #c084fc 0%, #7c3aed 100%)',
                      padding: '4px 12px',
                      borderRadius: 12,
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#fff',
                      letterSpacing: 0.5,
                    }}
                  >
                    {pack.tag}
                  </div>
                )}

                {/* Pack content */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p
                      style={{
                        fontSize: 26,
                        fontWeight: 700,
                        color: pack.color,
                        letterSpacing: -0.5,
                        marginBottom: 4,
                      }}
                    >
                      {pack.coins.toLocaleString()}
                    </p>
                    <p style={{ fontSize: 12, color: 'rgba(226,232,240,0.4)' }}>
                      {pack.questions} questions
                    </p>
                  </div>

                  <motion.button
                    whileTap={{ scale: 0.96 }}
                    data-testid={`buy-${pack.id}-btn`}
                    disabled={!!loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!loading) handlePurchase(pack);
                    }}
                    style={{
                      background: isPopular
                        ? 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)'
                        : isBest
                        ? 'linear-gradient(135deg, #c084fc 0%, #7c3aed 100%)'
                        : 'rgba(116,179,255,0.15)',
                      border: isPopular || isBest ? 'none' : '1px solid rgba(116,179,255,0.3)',
                      borderRadius: 12,
                      padding: '12px 24px',
                      color: '#fff',
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: isProcessing ? 0.7 : 1,
                      minWidth: 100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: isPopular ? '0 4px 16px rgba(116,179,255,0.25)' : 'none',
                    }}
                  >
                    {isProcessing ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        style={{
                          width: 18,
                          height: 18,
                          border: '2px solid #fff',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                        }}
                      />
                    ) : (
                      `₹${pack.price.toLocaleString()}`
                    )}
                  </motion.button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Spacer */}
        <div style={{ marginBottom: 20 }} />

        {/* Subscription upsell */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          data-testid="view-plans-link"
          onClick={() => navigate('/payment-plans')}
          style={{
            textAlign: 'center',
            padding: '14px 20px',
            borderRadius: 14,
            background: 'rgba(116,179,255,0.04)',
            border: '1px solid rgba(116,179,255,0.08)',
            cursor: 'pointer',
            marginBottom: 24,
          }}
        >
          <p style={{ fontSize: 12, color: 'rgba(226,232,240,0.5)', marginBottom: 2 }}>
            Want more features?
          </p>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#74B3FF' }}>
            View Subscription Plans
          </p>
        </motion.div>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingBottom: 20 }}>
          <p style={{ fontSize: 10, color: 'rgba(226,232,240,0.2)', letterSpacing: 2, fontWeight: 600 }}>
            TARAANAYA 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditsRefill;
